import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import styles from "./TextareaModalField.module.scss";
import { Button } from "../Button/Button";

interface TooltipPortalProps {
  text: string;
  anchorRect: DOMRect | null;
  visible: boolean;
}

const TooltipPortal: React.FC<TooltipPortalProps> = ({ text, anchorRect, visible }) => {
  if (!anchorRect) return null;
  const style: React.CSSProperties = {
    position: "fixed",
    left: anchorRect.left,
    top: anchorRect.top - 6,
    transform: visible ? "translateY(calc(-100% - 0px))" : "translateY(calc(-100% + 6px))",
    width: anchorRect.width,
    opacity: visible ? 1 : 0,
    transition: "opacity .15s ease, transform .15s ease",
    pointerEvents: "none",
    zIndex: 2000
  };
  return createPortal(
    <div style={style} className={styles.hintTooltip}>
      {text}
    </div>,
    document.body
  );
};

interface HintItem {
  title: string;
  description: string;
}

export interface ExampleLine {
  key: string;
  value: string;
}

interface ExampleContent {
  lines: ExampleLine[];
  label?: string;
}

export interface TextareaModalFieldProps {
  name: string;
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minLength?: number;
  maxLength?: number;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  hintItems?: HintItem[];
  example?: ExampleContent;
  modalTitle?: string;
  dataFieldId?: string;
  showCompleteMark?: boolean;
  triggerWhiteBg?: boolean;
  saveBtnGreen?: boolean;
  isOldUI?: boolean;
  onBlockedClick?: () => void;
  onFocus?: () => void;
}

export const TextareaModalField: React.FC<TextareaModalFieldProps> = ({
  name,
  label,
  value,
  onChange,
  placeholder,
  minLength,
  maxLength = 10000,
  required,
  error,
  disabled,
  hintItems = [],
  example,
  modalTitle,
  dataFieldId,
  showCompleteMark,
  triggerWhiteBg,
  saveBtnGreen,
  isOldUI = false,
  onBlockedClick,
  onFocus
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [showExample, setShowExample] = useState(true);
  const [tooltip] = useState<{ text: string; rect: DOMRect | null; visible: boolean }>({
    text: "",
    rect: null,
    visible: false
  });
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const open = useCallback(() => {
    onFocus?.();
    setDraft(value);
    setShowExample(true);
    setShouldRender(true);
    requestAnimationFrame(() => setIsAnimating(true));
    setIsOpen(true);
  }, [value, onFocus]);

  const close = useCallback(() => {
    setIsAnimating(false);
    setIsOpen(false);
    setTimeout(() => setShouldRender(false), 300);
  }, []);

  const save = useCallback(() => {
    onChange(draft);
    close();
  }, [draft, onChange, close]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [isOpen, close]);

  useEffect(() => {
    if (isOpen && isAnimating) {
      const t = setTimeout(() => textareaRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isOpen, isAnimating]);

  const charCount = draft.length;
  const meetsMin = minLength === undefined || charCount >= minLength;
  const hasValue = value.length > 0;
  const isComplete = hasValue && (minLength === undefined || value.length >= minLength);

  const previewText = hasValue ? (value.length > 100 ? value.slice(0, 100) + "…" : value) : null;

  const hintsTotal = hintItems.length;

  return (
    <>
      <div
        className={`${styles.wrapper} ${!error && isOldUI ? styles.noErrorWrapper : ""}`}
        data-field-id={dataFieldId}
      >
        <div
          className={`${styles.triggerContainer} ${error ? styles.hasError : ""} ${
            disabled ? styles.disabled : ""
          } ${triggerWhiteBg ? styles.triggerWhiteBg : ""}`}
        >
          <button
            type="button"
            className={`${styles.triggerButton} ${isOldUI ? styles.triggerButtonOldUI : ""}`}
            onClick={disabled ? undefined : onBlockedClick ?? open}
            disabled={disabled}
            data-field-id={dataFieldId}
          >
            <div className={styles.triggerButtonLabel}>
              <span className={`${styles.triggerLabel} ${isOldUI ? styles.triggerLabelOldUI : ""}`}>
                {label}
                {required && (
                  <span className={`${styles.required} ${isOldUI ? styles.requiredOldUI : ""}  `}>
                    *
                  </span>
                )}
                {isComplete && showCompleteMark && <span className={styles.completeMark}>✓</span>}
              </span>
              {previewText && <span className={styles.previewText}>{previewText}</span>}
            </div>
            <span className={styles.triggerArrow}>→</span>
          </button>
        </div>

        {error && isOldUI && <span className={styles.errorMsg}>{error}</span>}
      </div>

      {shouldRender &&
        createPortal(
          <div
            className={`${styles.overlay} ${isAnimating ? styles.overlayActive : ""}`}
            onClick={close}
          >
            <div
              className={`${styles.modal} ${isAnimating ? styles.modalActive : ""}`}
              onClick={e => e.stopPropagation()}
            >
              <div className={styles.modalHeader}>
                <h3 className={styles.modalTitle}>{modalTitle || "Описание"}</h3>
                <button
                  type="button"
                  className={styles.closeBtn}
                  onClick={close}
                  aria-label="Закрыть"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <div className={styles.modalBody}>
                <div className={styles.editorPane}>
                  {example && (
                    <div
                      className={`${styles.exampleArea} ${
                        showExample ? styles.exampleAreaOpen : ""
                      }`}
                    >
                      <div className={styles.exampleBox}>
                        <p className={styles.exampleTitle}>Пример заполнения:</p>
                        <div className={styles.exampleLines}>
                          {example.lines.map((line, i) => (
                            <p key={i} className={styles.exampleLine}>
                              <span className={styles.exampleKey}>{line.key}: </span>
                              <span className={styles.exampleValue}>{line.value}</span>
                            </p>
                          ))}
                        </div>
                        <p className={styles.exampleDescription}>
                          Чем подробнее описание, тем быстрее менеджер подтвердит подключение к
                          Prodamus
                        </p>
                      </div>
                    </div>
                  )}

                  <textarea
                    ref={textareaRef}
                    className={`${styles.textarea} ym-disable-keys`}
                    name={name}
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    placeholder={placeholder || "Напишите о своей деятельности"}
                    maxLength={maxLength}
                  />
                </div>

                {hintItems.length > 0 && (
                  <div className={styles.hintsPane}>
                    <div className={styles.hintsPaneHeader}>
                      <span className={styles.hintsPaneTitle}>Как заполнить?</span>
                      <span className={styles.hintsCount}>0 из {hintsTotal}</span>
                    </div>

                    {/* Hints list */}
                    <ul className={styles.hintsList}>
                      {hintItems.map((item, i) => (
                        <li key={i} className={styles.hintItem}>
                          <span className={styles.hintNum}>{i + 1}</span>
                          <div className={styles.hintContent}>
                            <span className={styles.hintTitle}>{item.title}</span>
                            <span className={styles.hintDesc}>
                              {item.description.split("\n").map((part, j, arr) => (
                                <span
                                  key={j}
                                  style={{
                                    display: "block",
                                    marginBottom: j < arr.length - 1 ? 8 : 0
                                  }}
                                >
                                  {part}
                                </span>
                              ))}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className={styles.modalFooter}>
                <Button
                  type="submit"
                  variant="primary-alt"
                  disabled={Boolean(minLength! - charCount) || !charCount}
                  shineAnimation
                  shineAnimationDelay={10}
                  className={`${styles.saveBtn} ${saveBtnGreen ? styles.saveBtnGreen : ""}`}
                  onClick={save}
                >
                  {meetsMin ? "Продолжить" : `Ещё ${minLength! - charCount} симв.`}
                </Button>
              </div>
            </div>
          </div>,
          document.body
        )}
      <TooltipPortal text={tooltip.text} anchorRect={tooltip.rect} visible={tooltip.visible} />
    </>
  );
};
